// Capa local y reglas de pintado de las marcas de «tramo revisado» (spec
// 2026-10-27 § 9). Todo puro: el hook `useSegmentReviewState` solo las orquesta.
import type { RouteSegment } from '../types/surveillanceRoute';
import type { AutoReview } from './segmentAutoReview';

/**
 * Último gesto del usuario sobre un tramo. `token` identifica el gesto,
 * `changedAt` es su hora (la misma que viaja en la mutation y que el servidor
 * guarda como `reviewedAt`) y `previous` lo que había antes, para restaurarlo
 * si el guardado falla.
 */
export type LocalReview = {
  reviewed: boolean;
  token: number;
  changedAt: string;
  previous?: LocalReview;
};
export type LocalReviewMap = ReadonlyMap<string, LocalReview>;

/** Cómo se pinta la casilla de un tramo revisable. */
export type ReviewDisplay = 'reviewed' | 'changed' | 'auto' | 'none';

/** Registra un gesto: la capa local gana siempre (es lo último que tocó). */
export function applyGesture(
  local: LocalReviewMap,
  ids: string[],
  reviewed: boolean,
  token: number,
  changedAt: string
): LocalReviewMap {
  const next = new Map(local);
  for (const id of ids) {
    next.set(id, { reviewed, token, changedAt, previous: local.get(id) });
  }
  return next;
}

/**
 * Deshace un gesto fallido SOLO donde sigue vigente (mismo token) y RESTAURA lo
 * que había antes —no lo borra—: si el tramo estaba marcado y falla el
 * desmarcado, debe seguir viéndose marcado. Un toque posterior lleva otro token
 * y se respeta.
 */
export function rollbackGesture(
  local: LocalReviewMap,
  ids: string[],
  token: number
): LocalReviewMap {
  let next: Map<string, LocalReview> | null = null;
  for (const id of ids) {
    const entry = local.get(id);
    if (entry?.token !== token) continue;
    next ??= new Map(local);
    if (entry.previous) next.set(id, entry.previous);
    else next.delete(id);
  }
  return next ?? local;
}

/**
 * El servidor manda: si para un tramo respondió otro estado que el gesto (había
 * uno más reciente), la capa local adopta el suyo. Solo si el gesto sigue vigente.
 */
export function adoptServerResults(
  local: LocalReviewMap,
  token: number,
  results: ReadonlyArray<{ segmentId: string; reviewed: boolean }>
): LocalReviewMap {
  let next: Map<string, LocalReview> | null = null;
  for (const { segmentId, reviewed } of results) {
    const entry = local.get(segmentId);
    if (!entry || entry.token !== token || entry.reviewed === reviewed) continue;
    next ??= new Map(local);
    next.set(segmentId, { ...entry, reviewed, previous: undefined });
  }
  return next ?? local;
}

/**
 * Retención de lo encolado: cuando una marca aparece en la cola se copia a la
 * capa local (token 0), de modo que al vaciarse la cola tras reproducirse, y
 * antes de que el detalle revalide, la casilla no parpadea. Lo que ya está en
 * la capa local manda (es un gesto posterior).
 */
export function retainPending(
  local: LocalReviewMap,
  pending: ReadonlyMap<string, boolean>
): LocalReviewMap {
  let next: Map<string, LocalReview> | null = null;
  pending.forEach((reviewed, id) => {
    if (local.has(id)) return;
    next ??= new Map(local);
    next.set(id, { reviewed, token: 0, changedAt: '' });
  });
  return next ?? local;
}

/**
 * ¿La marca del servidor cuenta como revisada y al día? Única definición de la
 * regla «myReview vivo y no obsoleto» (la usan también las utils de progreso).
 */
export const isFreshReview = (segment: RouteSegment): boolean =>
  !!segment.myReview && !segment.myReview.stale;

/** ¿Lo que dice el servidor ya coincide con lo que la capa local afirma? */
const serverAgrees = (segment: RouteSegment, reviewed: boolean): boolean =>
  reviewed ? isFreshReview(segment) : !segment.myReview;

/**
 * ¿El servidor ya procesó este gesto o uno más nuevo? `reviewedAt` es la hora
 * del aparato que viajó en la mutation, así que `>=` vale (ISO UTC: comparable).
 * Los gestos retenidos de la cola (sin hora) no se sueltan por esta vía.
 */
const serverProcessed = (segment: RouteSegment, entry: LocalReview): boolean =>
  !!entry.changedAt &&
  !!segment.myReview &&
  segment.myReview.reviewedAt >= entry.changedAt;

/**
 * Suelta lo que el detalle ya refleja. Sin esto una marca local quedaría
 * fijada para siempre y taparía un «ha cambiado después» posterior o una
 * corrección del servidor: se suelta si coincide O si el servidor ya procesó
 * ese gesto (o uno más nuevo), diga lo que diga.
 */
export function pruneSettled(
  local: LocalReviewMap,
  segments: RouteSegment[]
): LocalReviewMap {
  if (local.size === 0) return local;
  let next: Map<string, LocalReview> | null = null;
  for (const segment of segments) {
    const entry = local.get(segment.id);
    if (!entry) continue;
    if (
      !serverAgrees(segment, entry.reviewed) &&
      !serverProcessed(segment, entry)
    ) {
      continue;
    }
    next ??= new Map(local);
    next.delete(segment.id);
  }
  return next ?? local;
}

/** Superposición final: lo encolado, tapado por la capa local. */
export function mergeOverlay(
  pending: ReadonlyMap<string, boolean>,
  local: LocalReviewMap
): ReadonlyMap<string, boolean> {
  const merged = new Map<string, boolean>(pending);
  local.forEach((entry, id) => merged.set(id, entry.reviewed));
  return merged;
}

/**
 * Estado visible de la casilla. La superposición manda y nunca es ámbar; si no
 * la hay, la marca del servidor: viva = revisado, obsoleta = «ha cambiado».
 */
export function getReviewDisplay(
  segment: RouteSegment,
  overlay?: ReadonlyMap<string, boolean>,
  autoReviews?: ReadonlyMap<string, AutoReview>
): ReviewDisplay {
  // Revisado por una actuación: manda sobre la marca local y la del servidor.
  if (autoReviews?.has(segment.id)) return 'auto';
  const override = overlay?.get(segment.id);
  if (override !== undefined) return override ? 'reviewed' : 'none';
  if (!segment.myReview) return 'none';
  return isFreshReview(segment) ? 'reviewed' : 'changed';
}
